import { useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import "../Landing.css";
import {
  CommonSections,
  Feature,
  TrustStrip,
} from "./_shared";

/**
 * Modèle 1 — Réglette vivante.
 * Signature : le spectre A–G interactif du hero → facture (fourchette) + les 3
 * profils d'usage. Tout dans le langage Liquid Glass (tokens --lg-*).
 */

const CLASSES = [
  { c: "A", color: "#34c759", eur: 420 },
  { c: "B", color: "#86d94a", eur: 680 },
  { c: "C", color: "#cdd836", eur: 1050 },
  { c: "D", color: "#ffd60a", eur: 1450 },
  { c: "E", color: "#ff9f0a", eur: 1980 },
  { c: "F", color: "#ff6a2c", eur: 2640 },
  { c: "G", color: "#ff3b30", eur: 3380 },
] as const;

const eur = (n: number) => `${Math.round(n).toLocaleString("fr-FR")} €`;
const round10 = (n: number) => Math.round(n / 10) * 10;

export default function HomeReglette() {
  const [sel, setSel] = useState(3); // D par défaut
  const cur = CLASSES[sel];
  const min = round10(cur.eur * 0.85);
  const max = round10(cur.eur * 1.15);
  const profils = [
    { key: "Sobre", v: round10(cur.eur * 0.8) },
    { key: "Standard", v: cur.eur, std: true },
    { key: "Intensif", v: round10(cur.eur * 1.3) },
  ];

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      setSel((s) => Math.min(CLASSES.length - 1, s + 1));
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      setSel((s) => Math.max(0, s - 1));
    }
  };

  return (
    <Layout>
      <div className="home">
        {/* HERO */}
        <section className="home-wrap home-hero">
          <div className="home-glass home-hero-grid">
            <div>
              <span className="home-eyebrow">Diagnostic de performance énergétique</span>
              <h1 className="home-h1">
                Estimez votre DPE et votre facture énergie,{" "}
                <span className="home-accent">gratuitement</span>.
              </h1>
              <p className="home-sub">
                Fondée sur la même méthode de calcul (3CL-DPE 2021), en 30 minutes,
                sans rendez-vous ni diagnostiqueur qui se déplace.
              </p>
              <div className="home-cta-row">
                <Link to="/register" className="home-btn home-btn-primary">
                  Commencer mon estimation gratuite
                </Link>
                <Link to="/login" className="home-btn home-btn-ghost">
                  Se connecter
                </Link>
              </div>
            </div>

            {/* SIGNATURE : réglette vivante */}
            <div className="home-glass home-reglette">
              <div className="home-reglette-cap">
                Pour un logement type de 90 m² — touchez une classe :
              </div>
              <div
                className="home-spectrum"
                role="radiogroup"
                aria-label="Choisir une classe énergie pour estimer la facture"
                onKeyDown={onKey}
              >
                {CLASSES.map((cl, i) => (
                  <button
                    key={cl.c}
                    type="button"
                    role="radio"
                    aria-checked={i === sel ? "true" : "false"}
                    tabIndex={i === sel ? 0 : -1}
                    aria-label={`Classe ${cl.c}`}
                    className={`home-seg${i === sel ? " is-active" : ""}`}
                    onClick={() => setSel(i)}
                  >
                    <span className="bar" style={{ background: cl.color }} />
                    <span className="cls">{cl.c}</span>
                  </button>
                ))}
              </div>

              <div className="home-facture">
                <span className="big">≈ {eur(cur.eur)}<span style={{ fontSize: "0.5em", fontWeight: 600 }}> / an</span></span>
                <span className="range">fourchette {eur(min)} – {eur(max)}</span>
              </div>

              <ul className="home-profils">
                {profils.map((p) => (
                  <li key={p.key} className={`home-profil${p.std ? " is-std" : ""}`}>
                    <div className="k">{p.key}</div>
                    <div className="v">{eur(p.v)}</div>
                  </li>
                ))}
              </ul>
              <p className="home-reglette-note">
                Estimation indicative, abonnements compris. Votre facture réelle dépend
                aussi de votre usage — d'où les trois profils.
              </p>
            </div>
          </div>
        </section>

        <TrustStrip />

        {/* CE QUE VOUS OBTENEZ */}
        <section className="home-wrap home-section">
          <div className="home-section-head">
            <span className="home-eyebrow">Ce que vous obtenez</span>
            <h2>Bien plus qu'une lettre entre A et G.</h2>
          </div>
          <div className="home-grid-3">
            <Feature
              icon={<IconLabel />}
              title="Étiquette + facture"
              text="Vos classes énergie et GES, et surtout une facture annuelle en fourchette, abonnements compris."
            />
            <Feature
              icon={<IconLoss />}
              title="Où part la chaleur"
              text="Le détail de vos déperditions — murs, toiture, fenêtres — pour cibler les travaux qui comptent vraiment."
            />
            <Feature
              icon={<IconUsage />}
              title="Selon votre usage"
              text="Trois scénarios sobre / standard / intensif : la facture dépend autant du comportement que du bâti."
            />
          </div>
        </section>

        <CommonSections />
      </div>
    </Layout>
  );
}

function IconLabel() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
      <path d="M7 9h10M7 13h7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}
function IconLoss() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 20V10l8-6 8 6v10" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M9 20v-6h6v6" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  );
}
function IconUsage() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 18c3 0 3-8 6-8s3 8 6 8 4-4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
